import { CartAddEvent, ThemeEvents } from '@theme/events';

/**
 * Complete the look — a small cross-sell on the product page. Each recommended
 * product has its own size picker and adds straight to the cart, without leaving
 * the page. Prices are rendered server-side (so the store's currency app keeps
 * converting them); this component only handles selection and add-to-cart.
 *
 * When the shopper picks a sold-out size of the *main* product, the whole block
 * hides (and the restock form takes over), mirroring the "Sold out" button state.
 */
class CompleteTheLookComponent extends HTMLElement {
  /** @type {AbortController | undefined} */
  #controller;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.addEventListener('click', this.#onClick, { signal });
    this.addEventListener('change', this.#onChange, { signal });

    if (this.dataset.hideSoldoutVariant === 'true') {
      document.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    }

    for (const item of this.querySelectorAll('[data-ctl-item]')) this.#syncItem(item);
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }

  /**
   * Hide the block when the main product's selected variant is sold out.
   * @param {Event} event
   */
  #onVariantUpdate = (event) => {
    const detail = /** @type {any} */ (event).detail;
    if (detail?.data?.productId && this.dataset.productId && detail.data.productId !== this.dataset.productId) return;

    const available = detail?.resource?.available;
    if (typeof available === 'boolean') this.toggleAttribute('hidden', !available);
  };

  /** @param {Event} event */
  #onChange = (event) => {
    const select = event.target;
    if (select instanceof HTMLSelectElement && select.matches('[data-ctl-variant]')) {
      this.#syncItem(select.closest('[data-ctl-item]'));
    }
  };

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const button = target.closest('[data-ctl-add]');
    if (button instanceof HTMLButtonElement && !button.disabled) {
      event.preventDefault();
      this.#addToCart(button.closest('[data-ctl-item]'), button);
    }
  };

  /** Reflects the selected variant's availability onto the item's add button. @param {Element | null} item */
  #syncItem(item) {
    if (!item) return;

    const field = item.querySelector('[data-ctl-variant]');
    const button = item.querySelector('[data-ctl-add]');
    if (!(button instanceof HTMLButtonElement)) return;

    let available = true;
    if (field instanceof HTMLSelectElement) {
      available = field.selectedOptions[0]?.dataset.available === 'true';
    } else if (field instanceof HTMLInputElement) {
      available = field.dataset.available === 'true';
    }

    button.disabled = !available;
    button.toggleAttribute('data-ctl-soldout', !available);
  }

  /**
   * @param {Element | null} item
   * @param {HTMLButtonElement} button
   */
  async #addToCart(item, button) {
    const field = item?.querySelector('[data-ctl-variant]');
    const variantId = field instanceof HTMLInputElement || field instanceof HTMLSelectElement ? field.value : '';
    if (!variantId) return;

    button.setAttribute('data-ctl-loading', 'true');
    button.disabled = true;

    try {
      const response = await fetch(`${Theme.routes.cart_add_url}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
      });
      const data = await response.json();
      if (!response.ok || data.status) throw new Error(data.description || data.message || 'Cart error');

      document.dispatchEvent(new CartAddEvent({}, this.id, { source: 'complete-the-look-component', itemCount: 1 }));

      button.setAttribute('data-ctl-added', 'true');
      setTimeout(() => button.removeAttribute('data-ctl-added'), 2000);
    } catch (reason) {
      console.error('Complete the look:', reason);
      button.setAttribute('data-ctl-error', 'true');
      setTimeout(() => button.removeAttribute('data-ctl-error'), 2500);
    } finally {
      button.removeAttribute('data-ctl-loading');
      this.#syncItem(item);
    }
  }
}

if (!customElements.get('complete-the-look-component')) {
  customElements.define('complete-the-look-component', CompleteTheLookComponent);
}

export { CompleteTheLookComponent };
